import {
  STOREFRONT_TEMPLATE_IDS,
  STOREFRONT_TEMPLATE_INFO,
  type StorefrontTemplateId,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { cn } from "@loot-os/ui/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StorefrontSection } from "../components/StorefrontSection";
import { StorefrontTemplateThumbnail } from "../components/StorefrontTemplateThumbnail";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/** Which storefront template this environment's home page renders. */
export function StorefrontTemplatePage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();

  const save = useMutation(
    trpc.storefront.setTemplate.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success("Template saved.");
      },
    }),
  );

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment } = query.data;

  return (
    <StorefrontSection
      title="Template"
      description="The layout of this storefront's home page. Every template shows the same content; only the arrangement differs."
      environment={environment}
    >
      <TemplatePicker
        key={config.storefrontTemplate}
        saved={config.storefrontTemplate}
        canEdit={can("editor")}
        saving={save.isPending}
        onSave={(template) => save.mutate({ template })}
      />
    </StorefrontSection>
  );
}

/** One card per template; remount with `key` when the saved template changes. */
function TemplatePicker({
  saved,
  canEdit,
  saving,
  onSave,
}: {
  saved: StorefrontTemplateId;
  canEdit: boolean;
  saving: boolean;
  onSave: (template: StorefrontTemplateId) => void;
}) {
  const [chosen, setChosen] = useState(saved);

  return (
    <form
      className="grid gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(chosen);
      }}
    >
      <fieldset className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" disabled={!canEdit}>
        <legend className="sr-only">Storefront template</legend>
        {STOREFRONT_TEMPLATE_IDS.map((id) => {
          const info = STOREFRONT_TEMPLATE_INFO[id];
          const selected = chosen === id;
          return (
            <label
              key={id}
              className={cn(
                "group flex cursor-pointer flex-col gap-3 rounded-xl border bg-card p-3 transition-colors has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50 has-[:disabled]:cursor-default",
                selected ? "border-primary" : "border-border hover:border-primary/50",
              )}
            >
              <input
                type="radio"
                name="storefront-template"
                value={id}
                checked={selected}
                onChange={() => setChosen(id)}
                className="sr-only"
              />
              <div className="rounded-lg bg-background p-3">
                <StorefrontTemplateThumbnail
                  template={id}
                  className={cn(
                    "mx-auto h-56 w-auto",
                    selected ? "text-primary" : "text-muted-foreground",
                  )}
                />
              </div>
              <div className="grid gap-1 px-1 pb-1">
                <span className="flex items-center gap-2 font-medium">
                  {info.label}
                  {saved === id && <Badge variant="secondary">Current</Badge>}
                </span>
                <span className="text-sm text-muted-foreground">{info.description}</span>
              </div>
            </label>
          );
        })}
      </fieldset>
      {canEdit && (
        <div>
          <Button type="submit" disabled={saving || chosen === saved}>
            {saving ? "Saving…" : "Save template"}
          </Button>
        </div>
      )}
    </form>
  );
}
